import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import { useCategories, CategoryStructure } from "@hooks/useCategories";
import { CategoryCombobox } from "@components/transactions/TransactionSplitV2/CategoryCombobox";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { TransactionRuleActionFormSchema } from "../constants";
import { useEntityCategoryVisibility } from "../useEntityCategoryVisibility";

interface Props {
    index: number;
    controlButtonSize?: TriggerButtonProps["size"];
}

interface FormValues {
    actions: TransactionRuleActionFormSchema[];
}

export const CategoryActionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
}) => {
    const { values, setFieldValue } = useFormikContext<FormValues>();
    const allCategories = useCategories({ structure: CategoryStructure.FLAT });
    const { showBusiness, showPersonal } = useEntityCategoryVisibility(
        undefined,
        "actions",
    );

    const categories = useMemo(
        () =>
            allCategories.filter(
                (c) =>
                    (showPersonal && !c.isBusiness) ||
                    (showBusiness && c.isBusiness),
            ),
        [allCategories, showBusiness, showPersonal],
    );

    const categoriesById = useCategoriesById();
    const actionCategoryId = values.actions[index]?.value?.categoryId;
    const selectedCategory = actionCategoryId
        ? categoriesById[actionCategoryId]
        : undefined;

    return (
        <CategoryCombobox
            categories={categories}
            selectedCategory={selectedCategory}
            onChange={(value) =>
                setFieldValue(`actions.${index}.value`, {
                    categoryId: value?.id,
                })
            }
            hideFooter
            triggerButtonProps={{
                variant: "primary",
                size: controlButtonSize ?? "md",
            }}
        />
    );
};
